import { Check, Copy } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { fromRoman, toRoman } from "../lib/roman";

type Mode = "encode" | "decode";
const SAMPLE = "2024";

export function RomanTool() {
  const uid = useId();
  const [mode, setMode] = useState<Mode>("encode");
  const [input, setInput] = useState(SAMPLE);
  const [copied, setCopied] = useState(false);

  const result = useMemo<{ ok: boolean; text: string }>(() => {
    if (mode === "encode") {
      const t = input.trim();
      if (!/^\d+$/.test(t)) return { ok: false, text: "" };
      const roman = toRoman(Number(t));
      return roman === null
        ? { ok: false, text: "" }
        : { ok: true, text: roman };
    }
    const value = fromRoman(input);
    return value === null
      ? { ok: false, text: "" }
      : { ok: true, text: String(value) };
  }, [mode, input]);

  const copy = async () => {
    if (!result.ok || !result.text) return;
    try {
      await navigator.clipboard.writeText(result.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  return (
    <div className="space-y-6">
      <div className="inline-flex rounded-lg border border-border bg-card/60 p-1">
        <button
          type="button"
          aria-pressed={mode === "encode"}
          onClick={() => setMode("encode")}
          className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
            mode === "encode"
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {m.rn_mode_encode()}
        </button>
        <button
          type="button"
          aria-pressed={mode === "decode"}
          onClick={() => setMode("decode")}
          className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
            mode === "decode"
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {m.rn_mode_decode()}
        </button>
      </div>

      <div className="space-y-1.5 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <Label htmlFor={`${uid}-in`}>
          {mode === "encode" ? m.rn_input_number() : m.rn_input_roman()}
        </Label>
        <Input
          id={`${uid}-in`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={
            mode === "encode"
              ? m.rn_placeholder_encode()
              : m.rn_placeholder_decode()
          }
          spellCheck={false}
          autoComplete="off"
          className="font-mono text-lg"
        />
      </div>

      <div className="space-y-3 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold tracking-tight">
            {m.rn_output_label()}
          </h2>
          {result.ok && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={copy}
              className="gap-2"
            >
              {copied ? (
                <Check className="h-4 w-4 text-emerald-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
              {copied ? m.rn_copied() : m.rn_copy()}
            </Button>
          )}
        </div>
        {result.ok ? (
          <p
            data-testid="rn-output"
            className="break-all font-mono text-2xl text-foreground"
          >
            {result.text}
          </p>
        ) : (
          <p data-testid="rn-error" className="text-sm text-muted-foreground">
            {mode === "encode" ? m.rn_hint_number() : m.rn_hint_roman()}
          </p>
        )}
      </div>
    </div>
  );
}
